import React from 'react';
import { useDropzone } from 'react-dropzone';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { useHistory, useParams } from 'react-router-dom';

import { UxContext } from '@client/context/UxContext';
import useDateTimeFormatters from '@client/hooks/useDateTimeFormatters';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { type DateFormat, type DeviceSettings, type TimeFormat, type WifiApSettings, WIFI_AP_PASSPHRASE_REGEX, WIFI_AP_SSID_REGEX } from '@ledium/types';
import { BlockingOverlay, TitleAndDescription } from '@ledium/ui-common';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import {
    Alert,
    Container,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import axios from 'axios';

import CloudRestoreDialog, { type CloudBackupEntry } from './CloudRestoreDialog';

interface UpdatingStatus {
    downloading: boolean;
    percent?: number;
    speedKb?: number;
    eta?: number;
}

interface SettingsRouteParams {
    tabName?: string;
}

// exported for testing — window.location is non-configurable in vmThreads
export const _nav = { reload: () => window.location.assign('') };

ValidatorForm.addValidationRule('validSsid', (value: string) => WIFI_AP_SSID_REGEX.test(value));
ValidatorForm.addValidationRule('validPassphrase', (value: string) => WIFI_AP_PASSPHRASE_REGEX.test(value));

const defaultFormData = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
};

const defaultWifiApData = {
    ssid: '',
    passphrase: ''
};

const Settings = () => {
    const { handleSnackbar, handleAlert } = React.useContext(UxContext)!;
    const {
        globalState: { socket, dateFormat: savedDateFormat, timeFormat: savedTimeFormat, isOnline, serverTimeOffset },
        globalActions
    } = useGlobalStore();

    const [currentVersion, setCurrentVersion] = React.useState<string | null>(null);
    const [currentVersionIsNightly, setCurrentVersionIsNightly] = React.useState(false);
    const [remoteVersion, setRemoteVersion] = React.useState<string | null>(null);
    const [remoteVersionIsNightly, setRemoteVersionIsNightly] = React.useState(false);
    const [updating, setUpdating] = React.useState<UpdatingStatus | false>(false);
    const [formData, setFormData] = React.useState(defaultFormData);
    const [passwdChangeEnabled, setPasswdChangeEnabled] = React.useState(false);
    const [wifiApData, setWifiApData] = React.useState(defaultWifiApData);
    const [showPassphrase, setShowPassphrase] = React.useState(false);
    const [powerAction, setPowerAction] = React.useState<'reboot' | 'shutdown' | 'restart' | null>(null);
    const [deviceName, setDeviceName] = React.useState('');
    const [importFile, setImportFile] = React.useState<File | null>(null);
    const [importing, setImporting] = React.useState(false);
    const [restoring, setRestoring] = React.useState(false);
    const [offlineMode, setOfflineMode] = React.useState(false);
    const [nightlyBuild, setNightlyBuild] = React.useState(false);
    const [enableNightlyBuildInstalls, setEnableNightlyBuildInstalls] = React.useState(false);
    const [serialNumber, setSerialNumber] = React.useState('');
    const initialDt = React.useRef(new Date(Date.now() + serverTimeOffset));
    const [dtYear, setDtYear] = React.useState(initialDt.current.getFullYear());
    const [dtMonth, setDtMonth] = React.useState(initialDt.current.getMonth() + 1);
    const [dtDay, setDtDay] = React.useState(initialDt.current.getDate());
    const [dtHour24, setDtHour24] = React.useState(initialDt.current.getHours());
    const [dtHour12, setDtHour12] = React.useState(initialDt.current.getHours() % 12 || 12);
    const [dtAmPm, setDtAmPm] = React.useState<'AM' | 'PM'>(initialDt.current.getHours() < 12 ? 'AM' : 'PM');
    const [dtMinute, setDtMinute] = React.useState(initialDt.current.getMinutes());
    const [dtSecond, setDtSecond] = React.useState(initialDt.current.getSeconds());
    const [cloudBackupEnabled, setCloudBackupEnabled] = React.useState(false);
    const [cloudRestoreOpen, setCloudRestoreOpen] = React.useState(false);
    const [cloudBackups, setCloudBackups] = React.useState<CloudBackupEntry[]>([]);
    const [cloudBackupsLoading, setCloudBackupsLoading] = React.useState(false);
    const [helperAvailable, setHelperAvailable] = React.useState<boolean | null>(null);
    const history = useHistory();
    const params = useParams<SettingsRouteParams>();

    const validTabs = ['application', 'backup', 'user', 'system'] as const;
    const activeTab = (validTabs.includes(params.tabName as (typeof validTabs)[number]) ? params.tabName : 'application') as (typeof validTabs)[number];
    const [dateFormat, setDateFormat] = React.useState<DateFormat>(savedDateFormat ?? 'YYYY-MM-DD');
    const [timeFormat, setTimeFormat] = React.useState<TimeFormat>(savedTimeFormat ?? '24');
    const dateTimeSettingsDirty = dateFormat !== (savedDateFormat ?? 'YYYY-MM-DD') || timeFormat !== (savedTimeFormat ?? '24');
    const isDtDirty =
        dtYear !== initialDt.current.getFullYear() ||
        dtMonth !== initialDt.current.getMonth() + 1 ||
        dtDay !== initialDt.current.getDate() ||
        (timeFormat === '24'
            ? dtHour24 !== initialDt.current.getHours()
            : dtHour12 !== (initialDt.current.getHours() % 12 || 12) || dtAmPm !== (initialDt.current.getHours() < 12 ? 'AM' : 'PM')) ||
        dtMinute !== initialDt.current.getMinutes() ||
        dtSecond !== initialDt.current.getSeconds();

    const { formatDate, formatTime } = useDateTimeFormatters();

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { 'application/zip': ['.zip'] },
        multiple: false,
        onDrop: (accepted) => {
            if (accepted[0]) {
                setImportFile(accepted[0]);
            }
        }
    });
    const wifiApValid = WIFI_AP_SSID_REGEX.test(wifiApData.ssid) && WIFI_AP_PASSPHRASE_REGEX.test(wifiApData.passphrase);

    React.useEffect(() => {
        socket!.on('updating', setUpdating);
        socket!.on('update_done', setUpdating);

        return () => {
            socket!.off('updating');
            socket!.off('update_done');
        };
    }, [socket]);

    React.useEffect(() => {
        setPasswdChangeEnabled(!!(formData.currentPassword && formData.newPassword && formData.newPassword === formData.confirmPassword));
    }, [formData]);

    React.useEffect(() => {
        lediumApi.get<{ available: boolean }>('settings/helperAvailable').then((data) => {
            setHelperAvailable(data?.available ?? false);
        });
    }, []);

    React.useEffect(() => {
        lediumApi.get<DeviceSettings>('settings/').then((data) => {
            if (data) {
                setDeviceName(data.deviceName ?? '');
                setCloudBackupEnabled(data.cloudBackupEnabled ?? false);
                setOfflineMode(data.offlineMode ?? false);
                setNightlyBuild(data.nightlyBuild ?? false);
                setEnableNightlyBuildInstalls(data.enableNightlyBuildInstalls ?? false);
                setSerialNumber(data.serialNumber ?? '');
            }
        });
    }, []);

    React.useEffect(() => {
        lediumApi.get<WifiApSettings>('settings/getWifiAp').then((data) => {
            if (data) {
                setWifiApData(data);
            }
        });
    }, []);

    const fetchLatestVersion = React.useCallback(async () => {
        setRemoteVersion(null);
        const data = await lediumApi.get<{ latestVersion: string; nightly: boolean }>('update/getLatestVersion');
        setRemoteVersion(data?.latestVersion ?? '');
        setRemoteVersionIsNightly(data?.nightly ?? false);
    }, []);

    React.useEffect(() => {
        lediumApi.get<{ version: string; isNightly: boolean }>('update/getVersion').then((data) => {
            if (data) {
                setCurrentVersion(data.version);
                setCurrentVersionIsNightly(data.isNightly);
            }
        });
        fetchLatestVersion();
    }, [fetchLatestVersion]);

    async function update() {
        if (remoteVersionIsNightly) {
            return lediumApi.get(`update/download/nightly/${remoteVersion}`);
        }
        return lediumApi.get(`update/download/${remoteVersion}`);
    }

    // React.useEffect(() => {
    //     const interval = setInterval(async () => {
    //         lediumApi.get(`update/getVersion`).then(data => {
    //             if (data.version !== currentVersion) {
    //                 window.location.assign('');
    //             }
    //         });
    //
    //     }, 2000);
    //     return () => clearInterval(interval);
    // });

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        const name = event.target.name;
        setFormData({ ...formData, ...{ [name]: event.target.value } });
    }

    function onDeviceNameSave() {
        lediumApi.patch<{ success?: boolean }>('settings/deviceName', { deviceName }).then((data) => {
            if (data?.success === true) {
                handleSnackbar({ message: i18n('device_name_saved') as string });
            }
        });
    }

    function onCloudRestore(backup: CloudBackupEntry) {
        handleAlert({
            title: i18n('cloud_restore_confirm_title') as string,
            message: i18n('cloud_restore_confirm_message', backup.version, backup.date) as string,
            okText: i18n('cloud_restore_cta') as string,
            cancelText: i18n('cancel') as string,
            okCallback: async () => {
                setCloudRestoreOpen(false);
                setImporting(true);
                try {
                    const response = await axios.post<{ success: boolean; error?: string }>('/api/settings/cloudRestore', { filename: backup.filename });
                    if (response.data.success) {
                        setRestoring(true);
                    } else {
                        const key = `import_database_error_${response.data.error ?? 'unknown'}`;
                        handleSnackbar({
                            message: (i18n(key) as string) || (i18n('import_database_error_unknown') as string),
                            severity: 'error'
                        });
                    }
                } catch {
                    handleSnackbar({ message: i18n('import_database_error_unknown') as string, severity: 'error' });
                } finally {
                    setImporting(false);
                }
            }
        });
    }

    function onDateTimeFormatSave() {
        lediumApi.patch<{ success?: boolean }>('settings/dateTimeFormat', { dateFormat, timeFormat }).then((data) => {
            if (data?.success === true) {
                globalActions.api.setDateTimeFormat(dateFormat, timeFormat);
                handleSnackbar({ message: i18n('date_time_format_saved') as string });
            }
        });
    }

    async function onSetDateTime() {
        let hour = dtHour24;
        if (timeFormat === '12') {
            hour = dtAmPm === 'AM' ? dtHour12 % 12 : (dtHour12 % 12) + 12;
        }
        const date = new Date(dtYear, dtMonth - 1, dtDay, hour, dtMinute, dtSecond);
        const timestamp = Math.floor(date.getTime() / 1000);
        const data = await lediumApi.post<{ success?: boolean; serverTime?: number }>('settings/setDateTime', { timestamp });
        if (data?.success === true && data.serverTime !== undefined) {
            const pad = (n: number) => String(n).padStart(2, '0');
            const serverNow = new Date(data.serverTime);
            const timeStr = `${pad(serverNow.getHours())}:${pad(serverNow.getMinutes())}:${pad(serverNow.getSeconds())}`;
            handleSnackbar({ message: i18n('date_time_set', formatDate(data.serverTime), formatTime(timeStr)) as string });
            globalActions.api.setServerTimeOffset(data.serverTime);
            initialDt.current = serverNow;
        }
    }

    function onOfflineModeToggle(event: React.ChangeEvent<HTMLInputElement>) {
        const enabled = event.target.checked;
        setOfflineMode(enabled);
        lediumApi.patch('settings/offlineMode', { enabled });
    }

    function onCloudBackupToggle(event: React.ChangeEvent<HTMLInputElement>) {
        const enabled = event.target.checked;
        setCloudBackupEnabled(enabled);
        lediumApi.patch('settings/cloudBackupEnabled', { enabled });
    }

    async function onEnableNightlyBuildInstallsToggle(event: React.ChangeEvent<HTMLInputElement>) {
        const enabled = event.target.checked;
        setEnableNightlyBuildInstalls(enabled);
        await lediumApi.patch('settings/enableNightlyBuildInstalls', { enabled });
        fetchLatestVersion();
    }

    function handleWifiApChange(event: React.ChangeEvent<HTMLInputElement>) {
        const name = event.target.name;
        setWifiApData({ ...wifiApData, ...{ [name]: event.target.value } });
    }

    function onWifiApSubmit() {
        lediumApi.post<{ success?: boolean }>('settings/setWifiAp', wifiApData).then((data) => {
            if (data?.success === true) {
                handleSnackbar({ message: i18n('wifi_ap_updated') as string });
            }
        });
    }

    function onReboot() {
        handleAlert({
            title: i18n('reboot') as string,
            message: i18n('reboot_confirm') as string,
            okCallback: async () => {
                const data = await lediumApi.post<{ success: boolean }>('settings/reboot', {});
                if (data?.success) {
                    setPowerAction('reboot');
                }
            }
        });
    }

    function onShutdown() {
        handleAlert({
            title: i18n('shutdown') as string,
            message: i18n('shutdown_confirm') as string,
            okCallback: async () => {
                const data = await lediumApi.post<{ success: boolean }>('settings/halt', {});
                if (data?.success) {
                    setPowerAction('shutdown');
                }
            }
        });
    }

    function onRestartServerApp() {
        handleAlert({
            title: i18n('restart_server_app') as string,
            message: i18n('restart_server_app_confirm') as string,
            okCallback: async () => {
                const data = await lediumApi.post<{ success: boolean }>('settings/restartServerApp', {});
                if (data?.success) {
                    setPowerAction('restart');
                }
            },
            cancelText: i18n('cancel') as string
        });
    }

    async function onImport() {
        if (!importFile) {
            return;
        }
        setImporting(true);
        try {
            const form = new FormData();
            form.append('database', importFile, importFile.name);
            const response = await axios.post<{ success: boolean; error?: string }>('/api/settings/importDatabase', form);
            if (response.data.success) {
                setRestoring(true);
            } else {
                const key = `import_database_error_${response.data.error ?? 'unknown'}`;
                handleSnackbar({
                    message: (i18n(key) as string) || (i18n('import_database_error_unknown') as string),
                    severity: 'error'
                });
            }
        } catch {
            handleSnackbar({ message: i18n('import_database_error_unknown') as string, severity: 'error' });
        } finally {
            setImporting(false);
        }
    }

    function onSubmit() {
        lediumApi.post<{ auth?: boolean; success?: boolean }>(`users/changePassword`, formData).then((data) => {
            if (data?.auth === false) {
                handleSnackbar({
                    message: i18n('password_incorrect') as string,
                    severity: 'error'
                });
            }
            if (data?.success === true) {
                handleSnackbar({
                    message: i18n('password_changed') as string
                });
                setFormData(defaultFormData);
            }
        });
    }

    const Updating = () =>
        updating && updating.downloading ? (
            <>
                <div style={{ width: '50%', display: 'inline-block' }}>
                    <LinearProgress variant="determinate" value={updating.percent ?? 0} />
                </div>
                <Typography variant="body2">{i18n('update_in_progress', updating.percent, updating.speedKb, updating.eta)}</Typography>
            </>
        ) : (
            <>
                <Typography variant="body2">{i18n('update_downloaded')}</Typography>
            </>
        );

    const IsUpdateAvailable = () => {
        if (!remoteVersion) {
            return <>{i18n('no_update_available')}</>;
        }
        if (!enableNightlyBuildInstalls && remoteVersionIsNightly) {
            return <>{i18n('no_update_available')}</>;
        }
        const toSort = (v: string) => {
            const [a, b, c] = v.split('.').map(Number);
            return a * 1000000 + b * 1000 + c;
        };
        const remoteSort = toSort(remoteVersion);
        const currentSort = toSort(currentVersion ?? '0.0.0');
        if (remoteSort < currentSort) {
            return <>{i18n('no_update_available')}</>;
        }
        if (remoteSort === currentSort) {
            // Same version: only allow if upgrading from nightly to stable
            if (remoteVersionIsNightly || !currentVersionIsNightly) {
                return <>{i18n('no_update_available')}</>;
            }
        }
        const label = remoteVersionIsNightly ? `${i18n('update_to_version', remoteVersion)} Nightly` : i18n('update_to_version', remoteVersion);
        return (
            <Button
                variant="contained"
                onClick={update}
                sx={remoteVersionIsNightly ? { backgroundColor: 'orange', '&:hover': { backgroundColor: '#e65c00' } } : undefined}
            >
                {label}
            </Button>
        );
    };

    const SearchingForUpdate = () => (
        <Typography variant="body2">{remoteVersion === null ? <>{i18n('searching_for_update')}</> : <IsUpdateAvailable />}</Typography>
    );

    function getDateFieldOrder(): Array<'year' | 'month' | 'day'> {
        if (dateFormat === 'YYYY-MM-DD') {
            return ['year', 'month', 'day'];
        }
        if (dateFormat === 'MM/DD/YYYY') {
            return ['month', 'day', 'year'];
        }
        return ['day', 'month', 'year'];
    }

    function getBlockingText(): string | null {
        if (powerAction === 'reboot') {
            return i18n('rebooting') as string;
        }
        if (powerAction === 'shutdown') {
            return i18n('shutting_down') as string;
        }
        if (powerAction === 'restart') {
            return i18n('restarting_server_app') as string;
        }
        if (updating !== false && !updating.downloading) {
            return i18n('installing_update') as string;
        }
        if (restoring) {
            return i18n('restoring_database') as string;
        }
        return null;
    }

    const helperUnavailable = helperAvailable === false;
    const serverOffline = !isOnline || offlineMode;
    const disabledSection = helperUnavailable ? { opacity: 0.5, pointerEvents: 'none' as const } : undefined;
    const offlineDisabledSection = serverOffline ? { opacity: 0.5, pointerEvents: 'none' as const } : undefined;
    const updateDisabledSection = helperUnavailable || serverOffline ? { opacity: 0.5, pointerEvents: 'none' as const } : undefined;
    const SectionWarningIcon = () => (helperUnavailable ? <WarningAmberIcon sx={{ color: 'warning.main', fontSize: '1.2em' }} /> : null);
    const SectionOfflineIcon = () => (serverOffline ? <WifiOffIcon sx={{ color: 'error.main', fontSize: '1.2em' }} /> : null);

    return (
        <>
            <BlockingOverlay text={getBlockingText()} />
            <Container style={{ paddingBottom: '20px' }}>
                <Grid container spacing={3}>
                    <TitleAndDescription />

                    {serialNumber && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle1">
                                {i18n('serial_number')}: <strong>{serialNumber}</strong>
                            </Typography>
                        </Grid>
                    )}

                    {(serverOffline || helperUnavailable) && (
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                            {serverOffline && (
                                <Alert severity="error" icon={<WifiOffIcon fontSize="inherit" />}>
                                    {i18n('server_offline_warning')}
                                </Alert>
                            )}
                            {helperUnavailable && <Alert severity="warning">{i18n('helper_unavailable_warning')}</Alert>}
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs value={activeTab} onChange={(_, value) => history.push(`/settings/${value}`)}>
                        <Tab label={i18n('tab_application')} value="application" />
                        <Tab label={i18n('tab_backup')} value="backup" />
                        <Tab label={i18n('tab_user')} value="user" />
                        <Tab label={i18n('tab_system')} value="system" />
                    </Tabs>
                </Box>

                {activeTab === 'application' && (
                    <Grid container spacing={3} style={{ marginTop: '20px' }}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SectionOfflineIcon />
                                <SectionWarningIcon />
                                <Typography variant="h2" color={helperUnavailable || serverOffline ? 'text.secondary' : undefined}>
                                    {i18n('software_update')}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                {i18n('software_update_description')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sx={updateDisabledSection}>
                            <Typography variant="body2">{currentVersion ? i18n('application_version', currentVersion) : null}</Typography>
                        </Grid>

                        <Grid item xs={12} sx={updateDisabledSection}>
                            {updating ? <Updating /> : <SearchingForUpdate />}
                        </Grid>

                        {nightlyBuild && (
                            <Grid item xs={12} sx={{ textAlign: 'center', ...updateDisabledSection }}>
                                <FormControlLabel
                                    control={<Switch checked={enableNightlyBuildInstalls} onChange={onEnableNightlyBuildInstallsToggle} color="primary" />}
                                    label={i18n('enable_nightly_build_installs') as string}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}

                {activeTab === 'application' && (
                    <>
                        <Divider style={{ marginTop: '32px' }} />

                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Typography variant="h2">{i18n('date_and_time')}</Typography>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('date_and_time_description')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container spacing={3} sx={{ maxWidth: '50%', mx: 'auto' }}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>{i18n('date_format')}</InputLabel>
                                            <Select
                                                value={dateFormat}
                                                label={i18n('date_format')}
                                                onChange={(e) => setDateFormat(e.target.value as typeof dateFormat)}
                                            >
                                                <MenuItem value="YYYY-MM-DD">{i18n('date_format_ymd')}</MenuItem>
                                                <MenuItem value="MM/DD/YYYY">{i18n('date_format_mdy')}</MenuItem>
                                                <MenuItem value="DD/MM/YYYY">{i18n('date_format_dmy')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>{i18n('time_format')}</InputLabel>
                                            <Select
                                                value={timeFormat}
                                                label={i18n('time_format')}
                                                onChange={(e) => setTimeFormat(e.target.value as typeof timeFormat)}
                                            >
                                                <MenuItem value="24">{i18n('time_format_24')}</MenuItem>
                                                <MenuItem value="12">{i18n('time_format_12')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                <Button variant="contained" color="primary" disabled={!dateTimeSettingsDirty} onClick={onDateTimeFormatSave}>
                                    {i18n('save_changes')}
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                )}

                {activeTab === 'backup' && (
                    <>
                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Typography variant="h2">{i18n('export_database')}</Typography>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('export_database_description')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                <Button variant="contained" color="primary" href="/api/settings/exportDatabase" download>
                                    {i18n('export_database_download')}
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '32px' }} />

                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Typography variant="h2">{i18n('import_database')}</Typography>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('import_database_description')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Box
                                    {...getRootProps()}
                                    sx={{
                                        border: '2px dashed',
                                        borderColor: isDragActive ? 'primary.main' : 'divider',
                                        borderRadius: 1,
                                        p: 4,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        bgcolor: isDragActive ? 'action.hover' : 'transparent',
                                        transition: 'border-color 0.2s, background-color 0.2s'
                                    }}
                                >
                                    <input {...getInputProps()} />
                                    <Typography variant="body2" color="text.secondary">
                                        {importFile ? importFile.name : i18n('import_database_drop_or_select')}
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={!importFile || importing}
                                    onClick={onImport}
                                    startIcon={importing ? <CircularProgress size={16} color="inherit" /> : undefined}
                                >
                                    {i18n('import_database_upload')}
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '32px' }} />

                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SectionOfflineIcon />
                                    <Typography variant="h2" color={serverOffline ? 'text.secondary' : undefined}>
                                        {i18n('cloud_backup')}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('cloud_backup_description')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ textAlign: 'center', ...offlineDisabledSection }}>
                                <FormControlLabel
                                    control={<Switch checked={cloudBackupEnabled} onChange={onCloudBackupToggle} color="primary" />}
                                    label={i18n('cloud_backup_enabled') as string}
                                />
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '32px' }} />

                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SectionOfflineIcon />
                                    <Typography variant="h2" color={serverOffline ? 'text.secondary' : undefined}>
                                        {i18n('cloud_restore')}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('cloud_restore_description')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ textAlign: 'center', ...offlineDisabledSection }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setCloudRestoreOpen(true);
                                        setCloudBackupsLoading(true);
                                        lediumApi.get<{ backups: CloudBackupEntry[] }>('settings/cloudBackups').then((data) => {
                                            setCloudBackups(data?.backups ?? []);
                                            setCloudBackupsLoading(false);
                                        });
                                    }}
                                >
                                    {i18n('cloud_restore_open')}
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                )}

                {activeTab === 'user' && (
                    <ValidatorForm onSubmit={onSubmit}>
                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Typography variant="h2">{i18n('change_password')}</Typography>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('change_password_description')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextValidator
                                    fullWidth
                                    label={i18n('current_password')}
                                    type="password"
                                    variant="outlined"
                                    name="currentPassword"
                                    onChange={handleChange}
                                    value={formData.currentPassword}
                                    validators={['required']}
                                    errorMessages={[i18n('this_field_is_required')]}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextValidator
                                    fullWidth
                                    label={i18n('new_password')}
                                    type="password"
                                    variant="outlined"
                                    name="newPassword"
                                    onChange={handleChange}
                                    value={formData.newPassword}
                                    validators={['required']}
                                    errorMessages={[i18n('this_field_is_required')]}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextValidator
                                    fullWidth
                                    label={i18n('confirm_password')}
                                    type="password"
                                    variant="outlined"
                                    name="confirmPassword"
                                    onChange={handleChange}
                                    value={formData.confirmPassword}
                                    validators={['required']}
                                    errorMessages={[i18n('this_field_is_required')]}
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                <Button disabled={!passwdChangeEnabled} variant="contained" color="primary" type="submit">
                                    {i18n('change_password')}
                                </Button>
                            </Grid>
                        </Grid>
                    </ValidatorForm>
                )}

                {activeTab === 'system' && (
                    <>
                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Typography variant="h2">{i18n('offline_mode')}</Typography>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('offline_mode_description')}
                                </Typography>
                                {isOnline && (
                                    <Typography variant="body2" style={{ marginTop: '4px', color: 'darkred' }}>
                                        {i18n('offline_mode_online_warning')}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                <FormControlLabel
                                    control={<Switch checked={offlineMode} onChange={onOfflineModeToggle} color="primary" />}
                                    label={i18n('offline_mode_enabled') as string}
                                />
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '32px' }} />

                        {offlineMode && !isOnline && (
                            <>
                                <Grid container spacing={3} style={{ marginTop: '20px' }}>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SectionWarningIcon />
                                            <Typography variant="h2" color={helperUnavailable ? 'text.secondary' : undefined}>
                                                {i18n('set_date_and_time')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                            {i18n('set_date_and_time_description')}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} sx={disabledSection}>
                                        <Grid container spacing={3} sx={{ maxWidth: '50%', mx: 'auto' }}>
                                            {getDateFieldOrder().map((field) => {
                                                if (field === 'year') {
                                                    return (
                                                        <Grid key="year" item xs={4}>
                                                            <TextField
                                                                fullWidth
                                                                label={i18n('year') as string}
                                                                type="number"
                                                                value={dtYear}
                                                                onChange={(e) => setDtYear(Number(e.target.value))}
                                                                inputProps={{ min: 2000, max: 2099 }}
                                                            />
                                                        </Grid>
                                                    );
                                                }
                                                if (field === 'month') {
                                                    return (
                                                        <Grid key="month" item xs={4}>
                                                            <FormControl fullWidth>
                                                                <InputLabel>{i18n('month')}</InputLabel>
                                                                <Select
                                                                    value={dtMonth}
                                                                    label={i18n('month')}
                                                                    onChange={(e) => setDtMonth(Number(e.target.value))}
                                                                >
                                                                    {Array.from({ length: 12 }, (_, i) => (
                                                                        <MenuItem key={i + 1} value={i + 1}>
                                                                            {String(i + 1).padStart(2, '0')}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                    );
                                                }
                                                return (
                                                    <Grid key="day" item xs={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>{i18n('day')}</InputLabel>
                                                            <Select value={dtDay} label={i18n('day')} onChange={(e) => setDtDay(Number(e.target.value))}>
                                                                {Array.from({ length: 31 }, (_, i) => (
                                                                    <MenuItem key={i + 1} value={i + 1}>
                                                                        {String(i + 1).padStart(2, '0')}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sx={disabledSection}>
                                        <Grid container spacing={3} sx={{ maxWidth: timeFormat === '12' ? '70%' : '50%', mx: 'auto' }}>
                                            {timeFormat === '24' ? (
                                                <>
                                                    <Grid item xs={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>{i18n('hour')}</InputLabel>
                                                            <Select value={dtHour24} label={i18n('hour')} onChange={(e) => setDtHour24(Number(e.target.value))}>
                                                                {Array.from({ length: 24 }, (_, i) => (
                                                                    <MenuItem key={i} value={i}>
                                                                        {String(i).padStart(2, '0')}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>{i18n('minute')}</InputLabel>
                                                            <Select
                                                                value={dtMinute}
                                                                label={i18n('minute')}
                                                                onChange={(e) => setDtMinute(Number(e.target.value))}
                                                            >
                                                                {Array.from({ length: 60 }, (_, i) => (
                                                                    <MenuItem key={i} value={i}>
                                                                        {String(i).padStart(2, '0')}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>{i18n('second')}</InputLabel>
                                                            <Select
                                                                value={dtSecond}
                                                                label={i18n('second')}
                                                                onChange={(e) => setDtSecond(Number(e.target.value))}
                                                            >
                                                                {Array.from({ length: 60 }, (_, i) => (
                                                                    <MenuItem key={i} value={i}>
                                                                        {String(i).padStart(2, '0')}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </>
                                            ) : (
                                                <>
                                                    <Grid item xs={3}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>{i18n('hour')}</InputLabel>
                                                            <Select value={dtHour12} label={i18n('hour')} onChange={(e) => setDtHour12(Number(e.target.value))}>
                                                                {Array.from({ length: 12 }, (_, i) => (
                                                                    <MenuItem key={i + 1} value={i + 1}>
                                                                        {String(i + 1).padStart(2, '0')}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>{i18n('am_pm')}</InputLabel>
                                                            <Select
                                                                value={dtAmPm}
                                                                label={i18n('am_pm')}
                                                                onChange={(e) => setDtAmPm(e.target.value as 'AM' | 'PM')}
                                                            >
                                                                <MenuItem value="AM">AM</MenuItem>
                                                                <MenuItem value="PM">PM</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>{i18n('minute')}</InputLabel>
                                                            <Select
                                                                value={dtMinute}
                                                                label={i18n('minute')}
                                                                onChange={(e) => setDtMinute(Number(e.target.value))}
                                                            >
                                                                {Array.from({ length: 60 }, (_, i) => (
                                                                    <MenuItem key={i} value={i}>
                                                                        {String(i).padStart(2, '0')}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>{i18n('second')}</InputLabel>
                                                            <Select
                                                                value={dtSecond}
                                                                label={i18n('second')}
                                                                onChange={(e) => setDtSecond(Number(e.target.value))}
                                                            >
                                                                {Array.from({ length: 60 }, (_, i) => (
                                                                    <MenuItem key={i} value={i}>
                                                                        {String(i).padStart(2, '0')}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sx={{ textAlign: 'center', ...disabledSection }}>
                                        <Button variant="contained" color="primary" disabled={!isDtDirty} onClick={onSetDateTime}>
                                            {i18n('set_date_and_time_cta')}
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Divider style={{ marginTop: '32px' }} />
                            </>
                        )}

                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Typography variant="h2">{i18n('device_name')}</Typography>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('device_name_description')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label={i18n('device_name_label')}
                                    variant="outlined"
                                    value={deviceName}
                                    onChange={(event) => setDeviceName(event.target.value)}
                                    inputProps={{ maxLength: 64 }}
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                <Button disabled={!deviceName.trim()} variant="contained" color="primary" onClick={onDeviceNameSave}>
                                    {i18n('device_name_rename')}
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '32px' }} />

                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SectionWarningIcon />
                                    <Typography variant="h2" color={helperUnavailable ? 'text.secondary' : undefined}>
                                        {i18n('restart_server_app')}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('restart_server_app_description')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sx={{ ...disabledSection, textAlign: 'center' }}>
                                <Button variant="contained" color="warning" disabled={helperUnavailable} onClick={onRestartServerApp}>
                                    {i18n('restart_server_app_button')}
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '32px' }} />

                        <ValidatorForm onSubmit={onWifiApSubmit}>
                            <Grid container spacing={3} style={{ marginTop: '20px' }}>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SectionWarningIcon />
                                        <Typography variant="h2" color={helperUnavailable ? 'text.secondary' : undefined}>
                                            {i18n('wifi_ap_settings')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                        {i18n('wifi_ap_settings_description')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sx={disabledSection}>
                                    <Grid container spacing={3} sx={{ maxWidth: '50%', mx: 'auto' }}>
                                        <Grid item xs={12} md={6}>
                                            <TextValidator
                                                fullWidth
                                                label={i18n('wifi_ap_ssid')}
                                                variant="outlined"
                                                name="ssid"
                                                onChange={handleWifiApChange}
                                                value={wifiApData.ssid}
                                                validators={['required', 'validSsid']}
                                                errorMessages={[i18n('this_field_is_required'), i18n('wifi_ap_ssid_invalid')]}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextValidator
                                                fullWidth
                                                label={i18n('wifi_ap_passphrase')}
                                                type={showPassphrase ? 'text' : 'password'}
                                                variant="outlined"
                                                name="passphrase"
                                                onChange={handleWifiApChange}
                                                value={wifiApData.passphrase}
                                                validators={['required', 'validPassphrase']}
                                                errorMessages={[i18n('this_field_is_required'), i18n('wifi_ap_passphrase_invalid')]}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="toggle passphrase visibility"
                                                                onClick={() => setShowPassphrase((prev) => !prev)}
                                                                edge="end"
                                                            >
                                                                {showPassphrase ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                            <Button disabled={!wifiApValid || helperUnavailable} variant="contained" color="primary" type="submit">
                                                {i18n('wifi_ap_settings')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </ValidatorForm>

                        <Divider style={{ marginTop: '32px' }} />

                        <Grid container spacing={3} style={{ marginTop: '20px' }}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SectionWarningIcon />
                                    <Typography variant="h2" color={helperUnavailable ? 'text.secondary' : undefined}>
                                        {i18n('power_management')}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" style={{ marginTop: '4px' }}>
                                    {i18n('power_management_description')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sx={disabledSection}>
                                <Grid container sx={{ maxWidth: '50%', mx: 'auto', justifyContent: 'center' }}>
                                    <Grid item sx={{ display: 'flex', gap: '16px' }}>
                                        <Button variant="contained" color="warning" disabled={helperUnavailable} onClick={onReboot}>
                                            {i18n('reboot')}
                                        </Button>
                                        <Button variant="contained" color="error" disabled={helperUnavailable} onClick={onShutdown}>
                                            {i18n('shutdown')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Container>
            <CloudRestoreDialog
                isOpen={cloudRestoreOpen}
                onClose={() => setCloudRestoreOpen(false)}
                loading={cloudBackupsLoading}
                backups={cloudBackups}
                currentVersion={currentVersion}
                onRestore={onCloudRestore}
            />
        </>
    );
};

export default Settings;
